import PageShell from '@/components/feature/PageShell';
import PageIntro from '@/components/feature/PageIntro';
import FinalCta from '@/components/feature/FinalCta';
import FaqList from '@/components/feature/FaqList';
import Container from '@/components/base/Container';
import Eyebrow from '@/components/base/Eyebrow';
import InkSettle from '@/components/base/InkSettle';
import type { PriceDisplay, PricingComponent } from '@/types/customer';
import { pricingComponents, pricingFaqs, pricingStages } from '@/mocks/pricing';
import PublishedRanges from './components/PublishedRanges';

const displayLabel: Record<PriceDisplay, string> = {
  'confirmed-before-service': 'Confirmed before service',
  'quoted-after-diagnosis': 'Quoted after diagnosis',
  'approved-separately': 'Only with your approval',
};

export default function PricingPage() {
  const rows = pricingComponents as PricingComponent[];
  return (
    <PageShell>
      <PageIntro eyebrow="Pricing & Estimates" title="Clear pricing, no surprises.">
        See typical repair costs up front. Your technician confirms a final quote after diagnosing the issue.
      </PageIntro>

      <section className="bg-background-50 pb-20 md:pb-28">
        <Container>
          <Eyebrow label="How Pricing Works" />
          <div className="mt-10 grid grid-cols-1 gap-px overflow-hidden rounded-xl border border-background-200 bg-background-200 md:grid-cols-3">
            {pricingStages.map((s, i) => (
              <div key={s.title} className="bg-white p-6 md:p-8">
                <span className={`text-4xl md:text-5xl font-light leading-none ${i === 2 ? 'text-accent-500' : 'text-primary-400'}`}>{`0${i + 1}`}</span>
                <h3 className="mt-8 text-lg font-medium text-foreground-950">{s.title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-foreground-600">{s.text}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      <PublishedRanges />

      <section className="bg-background-100 py-20 md:py-28">
        <Container>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:gap-16">
            <Eyebrow label="Pricing Breakdown" />
            <div>
              <InkSettle className="text-[30px] md:text-[44px] font-medium leading-[1.05] tracking-[-0.025em] text-foreground-950">What makes up an estimate.</InkSettle>
              <p className="mt-4 max-w-md text-[15px] leading-relaxed text-foreground-600">Prices depend on the appliance, the fault and the parts needed, so the ranges above are a guide and your final quote is confirmed with you after diagnosis.</p>
            </div>
          </div>
          <div className="mt-12">
            {rows.map((r) => (
              <div key={r.id} className="mw-trace grid grid-cols-1 gap-2 border-t border-background-200 py-6 last:border-b md:grid-cols-12 md:items-center md:gap-6">
                <h3 className="mw-trace-text md:col-span-4 text-base md:text-lg font-medium text-foreground-950">{r.label}</h3>
                <p className="md:col-span-5 text-sm leading-relaxed text-foreground-600">{r.description}</p>
                <div className="md:col-span-3 md:text-right">
                  <span className="inline-flex whitespace-nowrap rounded-full bg-white px-3 py-1 text-xs text-foreground-800 border border-background-200">{displayLabel[r.display]}</span>
                </div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      <section className="bg-background-50 py-20 md:py-28">
        <Container>
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-16">
            <div className="lg:col-span-4">
              <Eyebrow label="FAQ" />
              <InkSettle className="mt-6 text-[30px] md:text-[40px] font-medium leading-[1.05] tracking-[-0.025em] text-foreground-950">Pricing questions.</InkSettle>
            </div>
            <div className="lg:col-span-8"><FaqList items={pricingFaqs} /></div>
          </div>
        </Container>
      </section>
      <FinalCta />
    </PageShell>
  );
}